import {
  BookingAddOnFormState,
  BookingAddOnRangesState,
  BookingAddOnSelection,
  BookingAddOnsMenuState,
  BookingAddOnsState,
  formDefinitionKey,
} from './types';

import { RootState } from 'store/reducer';
import { createSelector } from 'reselect';

const EMPTY_SELECTION: Array<BookingAddOnSelection> = [];

export const bookingAddOnsSelector = (state: RootState): BookingAddOnsState => state.bookingAddOns;

export const selectBookingAddOnsBookingId = createSelector([bookingAddOnsSelector], slice => slice.bookingId);

/*
 * BOOKING-SCOPED BY CONSTRUCTION. Returns the selection only when the slice is held by the booking being
 * asked about, and an empty array otherwise.
 *
 * This is deliberately belt-and-braces with the reducer's own guard: the reducer clears on claim, but that
 * depends on `startBookingAddOns` actually having been dispatched. Scoping the read as well means a stale
 * selection cannot render even if the claim never fired.
 */
export const selectAddOnsForBooking = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | null | undefined) => bookingId],
  (slice, bookingId) => (bookingId != null && slice.bookingId === String(bookingId) ? slice.addOns : EMPTY_SELECTION),
);

export const selectBookingAddOnsTotal = createSelector([selectAddOnsForBooking], addOns =>
  addOns.reduce((partialSum, addOn) => partialSum + addOn.price * addOn.quantity, 0),
);

/* When an order was placed against this booking, or null. Scoped against the marker's own booking id
 * rather than the slice's claim: by the time the card asks, the add-on surface has closed and the claim
 * may have moved on or been reset. */
export const selectAddOnsOrderedAt = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | number | null | undefined) => bookingId],
  (slice, bookingId) =>
    bookingId != null && slice.lastOrdered?.bookingId === String(bookingId) ? slice.lastOrdered.at : null,
);

/* The money the card was showing when the tenant left it for the picker, or null. Scoped like
 * `selectAddOnsOrderedAt`, and the preferred baseline: it is stamped while the card still holds the
 * figure, where the alternatives are a disabled query and a cache that is collected after 5 minutes. */
export const selectAddOnsAmendingFrom = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | number | null | undefined) => bookingId],
  (slice, bookingId) =>
    bookingId != null && slice.amendingFrom?.bookingId === String(bookingId) ? slice.amendingFrom.paymentKey : null,
);

/* The money as it stood just before that order was submitted, or null. Scoped like
 * `selectAddOnsOrderedAt`. The card withholds while the server still answers with this; the poll
 * keeps asking until it stops. */
export const selectAddOnsOrderedPaymentKey = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | number | null | undefined) => bookingId],
  (slice, bookingId) =>
    bookingId != null && slice.lastOrdered?.bookingId === String(bookingId) ? slice.lastOrdered.paymentKey : null,
);

/* When this booking's order was stood down, or null. Scoped like `selectAddOnsOrderedAt`. */
export const selectAddOnsCancelledAt = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | number | null | undefined) => bookingId],
  (slice, bookingId) =>
    bookingId != null && slice.lastCancelled?.bookingId === String(bookingId) ? slice.lastCancelled.at : null,
);

export const selectAddOnQuantityViolations = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | null | undefined) => bookingId],
  (slice, bookingId) =>
    bookingId != null && slice.bookingId === String(bookingId) ? slice.quantityViolations ?? [] : [],
);

export const selectSubmitBookingAddOnsStatus = createSelector(
  [bookingAddOnsSelector],
  slice => slice.submitBookingAddOns?.status ?? null,
);

const NO_FORM_FOR_BOOKING: BookingAddOnFormState = {
  status: null,
  templateUuid: null,
  definition: null,
  values: {},
  isValid: true,
  unresolvedItemIds: [],
  pendingKey: null,
};

/*
 * RES-1057. Booking-scoped for the same reason `selectAddOnsForBooking` is: the slice is a single slot
 * with a claim, so a read that does not check the claim can hand one booking's answers to another.
 * The reducer clears on claim, but only if `startBookingAddOns` actually fired.
 */
export const selectAddOnFormForBooking = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | null | undefined) => bookingId],
  (slice, bookingId) => (bookingId != null && slice.bookingId === String(bookingId) ? slice.form : NO_FORM_FOR_BOOKING),
);

const NO_RANGES: BookingAddOnRangesState = { status: null, byCatalogItemUuid: {}, pendingKey: null };

/*
 * RES-1311. Booking-scoped for the same reason the selection and the form are: one slot, one claim, so
 * an unscoped read can hand one booking's data to another. A wrong range is worse than none — it
 * bounds a control against a product the tenant is not looking at.
 */
export const selectAddOnRangesForBooking = createSelector(
  [bookingAddOnsSelector, (_: RootState, bookingId: string | null | undefined) => bookingId],
  (slice, bookingId) =>
    bookingId != null && slice.bookingId === String(bookingId) ? slice.ranges ?? NO_RANGES : NO_RANGES,
);

const NO_MENU: BookingAddOnsMenuState = { key: null, resource: null, status: null };

export const selectBookingAddOnsMenu = createSelector([bookingAddOnsSelector], slice => slice.menu ?? NO_MENU);

export const bookingAddOnsMenuKey = (resourceId: number | string, startAt: string): string =>
  `${resourceId}@${startAt}`;

/* Undefined means not read yet; null means read and there is none. */
export const selectAddOnFormDefinition = createSelector(
  [
    bookingAddOnsSelector,
    (_: RootState, templateUuid: string | null | undefined) => templateUuid,
    (_: RootState, __: string | null | undefined, lang: string) => lang,
  ],
  (slice, templateUuid, lang) =>
    templateUuid ? slice.formDefinitions?.[formDefinitionKey(templateUuid, lang)] : undefined,
);
